import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import { Box, Button } from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'

import { QuillHtmlParser } from 'src/components/quill-html-parser'

type DescriptionPreviewProps = {
  richHtml?: string
  children?: ReactNode
  collapsedHeight?: number
  fontSize?: string
  toggleLabels?: {
    expand: string
    collapse: string
  }
  toggleColor?: string
  className?: string
  contentKey?: string | number
  sx?: SxProps<Theme>
}

const DEFAULT_COLLAPSED_HEIGHT = 250

export function DescriptionPreview({
  richHtml,
  children,
  collapsedHeight = DEFAULT_COLLAPSED_HEIGHT,
  fontSize = '20px',
  toggleLabels = { expand: 'Show more', collapse: 'Show less' },
  toggleColor = 'primary.main',
  className,
  contentKey,
  sx,
}: DescriptionPreviewProps) {
  const [isExpanded, setIsExpanded] = useState(false)
  const [isOverflowing, setIsOverflowing] = useState(false)
  const [contentHeight, setContentHeight] = useState(0)
  const contentRef = useRef<HTMLDivElement | null>(null)

  const resolvedContentKey = useMemo(() => contentKey ?? richHtml, [contentKey, richHtml])

  const measureContent = useCallback(() => {
    const node = contentRef.current
    if (!node) {
      return
    }

    const scrollHeight = node.scrollHeight
    if (scrollHeight <= 0) {
      return
    }

    const shouldCollapse = scrollHeight > collapsedHeight + 4
    setContentHeight(scrollHeight)
    setIsOverflowing(shouldCollapse)
  }, [collapsedHeight])

  useEffect(() => {
    measureContent()
  }, [measureContent, resolvedContentKey, collapsedHeight])

  useEffect(() => {
    const node = contentRef.current
    if (!node) {
      return
    }

    const resizeObserver = new ResizeObserver(() => {
      measureContent()
    })

    resizeObserver.observe(node)

    return () => {
      resizeObserver.disconnect()
    }
  }, [measureContent])

  const handleToggle = () => {
    setIsExpanded((prev) => !prev)
  }

  const shouldClamp = isOverflowing && !isExpanded
  const maxHeightValue = !isOverflowing
    ? 'none'
    : `${isExpanded ? Math.max(contentHeight, collapsedHeight) : collapsedHeight}px`
  const clampMaskStyles = shouldClamp
    ? {
        WebkitMaskImage: 'linear-gradient(180deg, rgba(0,0,0,1) 60%, rgba(0,0,0,0))',
        maskImage: 'linear-gradient(180deg, rgba(0,0,0,1) 60%, rgba(0,0,0,0))',
      }
    : undefined
  return (
    <Box
      id="description-preview__components-description-preview__container__l1"
      className={className}
      sx={{ display: 'flex', flexDirection: 'column', gap: '4px', ...sx }}
    >
      <Box
        id="description-preview__components-description-preview__content__l8"
        ref={contentRef}
        sx={{
          position: 'relative',
          overflow: shouldClamp ? 'hidden' : 'visible',
          maxHeight: maxHeightValue,
          transition: 'max-height 0.35s ease',
          pb: 0,
          ...clampMaskStyles,
        }}
      >
        {richHtml ? <QuillHtmlParser html={richHtml} fontSize={fontSize} /> : children}
      </Box>

      {isOverflowing && !isExpanded && (
        <Button
          id="description-preview__components-description-preview__button__l32"
          variant="text"
          size="small"
          onClick={handleToggle}
          sx={{
            alignSelf: 'flex-start',
            fontWeight: 600,
            px: 0,
            color: toggleColor,
            '&:hover': { backgroundColor: 'transparent', color: toggleColor },
          }}
        >
          {toggleLabels.expand}
        </Button>
      )}
    </Box>
  )
}
